/**
 * Compact audio player for the product audio sample
 */
if (!customElements.get("ars-audio-sample")) {
  customElements.define("ars-audio-sample", class ArsAudioSample extends HTMLElement {

    connectedCallback() {
      this.audio = this.querySelector("audio")
      this.toggle = this.querySelector(".ars-audio-sample__toggle")
      this.wave = this.querySelector(".ars-audio-sample__wave")
      this.bars = [...this.querySelectorAll(".ars-audio-sample__bar")]
      this.time = this.querySelector(".ars-audio-sample__time")

      this.toggle.addEventListener("click", () => this.audio.paused ? this.audio.play() : this.audio.pause())
      this.wave.addEventListener("click", (event) => this.seekTo(event))
      this.wave.addEventListener("keydown", (event) => this.seekByKey(event))

      this.audio.addEventListener("loadedmetadata", () => this.render())
      this.audio.addEventListener("timeupdate", () => this.render())
      this.audio.addEventListener("play", () => this.setPlaying(true))
      this.audio.addEventListener("pause", () => this.setPlaying(false))
      this.audio.addEventListener("ended", () => {
        this.audio.currentTime = 0
        this.render()
      })

      // Metadata may already be loaded before the element is upgraded
      if (this.audio.readyState > 0) {
        this.render()
      }
    }

    /**
     * Format seconds as m:ss
     *
     * @param {number} seconds
     * @returns {string}
     */
    format(seconds) {
      if (!Number.isFinite(seconds)) {
        return "0:00"
      }
      const total = Math.round(seconds)
      return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`
    }

    setPlaying(playing) {
      this.classList.toggle("ars-audio-sample--playing", playing)
      this.toggle.setAttribute("aria-label", this.toggle.dataset[playing ? "labelPause" : "labelPlay"])
      // Stop all other audio samples on the page
      if (playing) {
        document.querySelectorAll("ars-audio-sample audio").forEach((audio) => {
          if (audio !== this.audio) {
            audio.pause()
          }
        })
      }
    }

    render() {
      const duration = this.audio.duration
      const current = this.audio.currentTime
      const progress = Number.isFinite(duration) && duration > 0 ? current / duration : 0
      const played = Math.round(progress * this.bars.length)

      this.bars.forEach((bar, index) => bar.classList.toggle("ars-audio-sample__bar--played", index < played))
      // Show the total duration until playback starts, afterwards the elapsed time
      this.time.textContent = current > 0 ? this.format(current) : this.format(duration)
      this.wave.setAttribute("aria-valuenow", Math.round(progress * 100))
      this.wave.setAttribute("aria-valuetext", `${this.format(current)} von ${this.format(duration)}`)
    }

    seekTo(event) {
      const duration = this.audio.duration
      if (!Number.isFinite(duration)) {
        return
      }
      // Die Balken stehen zentriert in der Wellenform, deshalb zählt nur der Bereich
      // vom ersten bis zum letzten sichtbaren Balken
      const visibleBars = this.bars.filter((bar) => bar.offsetWidth > 0)
      const left = visibleBars[0].getBoundingClientRect().left
      const right = visibleBars[visibleBars.length - 1].getBoundingClientRect().right
      const ratio = Math.min(Math.max((event.clientX - left) / (right - left), 0), 1)
      this.audio.currentTime = ratio * duration
      this.render()
    }

    seekByKey(event) {
      const duration = this.audio.duration
      if (!Number.isFinite(duration)) {
        return
      }
      const steps = { ArrowLeft: -5, ArrowDown: -5, ArrowRight: 5, ArrowUp: 5 }
      if (event.key in steps) {
        this.audio.currentTime = Math.min(Math.max(this.audio.currentTime + steps[event.key], 0), duration)
      } else if (event.key === "Home") {
        this.audio.currentTime = 0
      } else if (event.key === "End") {
        this.audio.currentTime = duration
      } else {
        return
      }
      event.preventDefault()
      this.render()
    }
  })
}
